import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Search,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  Plus,
  Trash2,
  Eye,
  Download,
  Loader2,
  Receipt,
} from "lucide-react";

const QuotesPage = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Create quote dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [savCases, setSavCases] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [quoteLines, setQuoteLines] = useState([
    { description: "", type: "piece", quantite: 1, prix_unitaire: 0, remise: 0 },
  ]);
  const [quoteRemise, setQuoteRemise] = useState(0);
  const [creating, setCreating] = useState(false);

  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [convertingQuoteId, setConvertingQuoteId] = useState(null);
  const [invoicePaymentMode, setInvoicePaymentMode] = useState("cb");

  const downloadPdf = async (url, filename) => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login"); return; }
    setDownloadingPdf(url);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Erreur téléchargement");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Erreur lors du téléchargement du PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter) params.statut = statusFilter;
      const response = await API.get("/quotes", { params });
      setQuotes(response.data.quotes);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchSavCases = async () => {
    try {
      const response = await API.get("/sav-cases", { params: { limit: 200 } });
      setSavCases(response.data.cases || []);
    } catch (error) {
      console.error("Error fetching SAV cases", error);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [statusFilter]);

  const openCreateDialog = () => {
    fetchSavCases();
    setSelectedCaseId("");
    setQuoteLines([{ description: "", type: "piece", quantite: 1, prix_unitaire: 0, remise: 0 }]);
    setQuoteRemise(0);
    setCreateDialogOpen(true);
  };

  const addLine = () => {
    setQuoteLines([...quoteLines, { description: "", type: "piece", quantite: 1, prix_unitaire: 0, remise: 0 }]);
  };

  const updateLine = (index, field, value) => {
    const updated = [...quoteLines];
    updated[index][field] = value;
    setQuoteLines(updated);
  };

  const removeLine = (index) => {
    if (quoteLines.length > 1) {
      setQuoteLines(quoteLines.filter((_, i) => i !== index));
    }
  };

  const calcTotal = () => {
    const subtotal = quoteLines.reduce((sum, l) => {
      return sum + l.prix_unitaire * l.quantite * (1 - l.remise / 100);
    }, 0);
    return subtotal * (1 - quoteRemise / 100);
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!selectedCaseId) {
      toast.error("Veuillez sélectionner un dossier SAV");
      return;
    }
    if (quoteLines.some((l) => !l.description || l.prix_unitaire <= 0)) {
      toast.error("Veuillez remplir toutes les lignes correctement");
      return;
    }
    setCreating(true);
    try {
      await API.post("/quotes", {
        sav_case_id: selectedCaseId,
        lignes: quoteLines,
        remise_globale: quoteRemise,
      });
      toast.success("Devis créé avec succès");
      setCreateDialogOpen(false);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (quoteId, status) => {
    try {
      await API.put(`/quotes/${quoteId}`, { statut: status });
      if (status === "envoye") {
        toast.success("Devis envoyé par email au client");
      } else {
        toast.success("Devis mis à jour");
      }
      fetchQuotes();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const convertToInvoice = async (quoteId) => {
    try {
      const response = await API.post(`/quotes/${quoteId}/convert-to-invoice`, {
        mode_paiement: invoicePaymentMode
      });
      const numero = response.data?.numero || "";
      toast.success(`Facture ${numero} créée avec succès`);
      setConvertingQuoteId(null);
      fetchQuotes();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de la conversion");
    }
  };

  const statusConfig = {
    brouillon: { label: "Brouillon", color: "bg-gray-100 text-gray-800", icon: FileText },
    envoye: { label: "Envoyé", color: "bg-blue-100 text-blue-800", icon: Send },
    accepte: { label: "Accepté", color: "bg-green-100 text-green-800", icon: CheckCircle },
    refuse: { label: "Refusé", color: "bg-red-100 text-red-800", icon: XCircle },
    expire: { label: "Expiré", color: "bg-yellow-100 text-yellow-800", icon: Clock },
    facture: { label: "Facturé", color: "bg-indigo-100 text-indigo-800", icon: Receipt },
  };

  const filteredQuotes = searchTerm
    ? quotes.filter(
        (q) =>
          q.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.sav_case_numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.client_nom?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : quotes;

  return (
    <div className="space-y-6" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Devis</h1>
          <p className="text-muted-foreground">{total} devis au total</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-quote-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nouveau devis
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher par numéro, client..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="quotes-search-input"
        />
      </div>

      {/* Stats */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
        {Object.entries(statusConfig).map(([key, config]) => {
          const count = quotes.filter((q) => q.statut === key).length;
          const Icon = config.icon;
          return (
            <Card
              key={key}
              className={`cursor-pointer transition-all ${statusFilter === key ? "ring-2 ring-primary" : ""}`}
              onClick={() => setStatusFilter(statusFilter === key ? "" : key)}
              data-testid={`filter-${key}`}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded-lg ${config.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground">{config.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Table - desktop */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : filteredQuotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="font-medium">Aucun devis trouvé</p>
              <Button variant="link" onClick={openCreateDialog}>
                Créer votre premier devis
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Numéro</TableHead>
                    <TableHead>Dossier SAV</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Total TTC</TableHead>
                    <TableHead>Facture</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredQuotes.map((quote) => {
                    const config = statusConfig[quote.statut] || statusConfig.brouillon;
                    return (
                      <TableRow key={quote.id} data-testid={`quote-row-${quote.id}`}>
                        <TableCell className="font-mono font-medium">{quote.numero}</TableCell>
                        <TableCell>
                          <span
                            className="text-primary hover:underline cursor-pointer"
                            onClick={() => {
                              const c = savCases.find((s) => s.id === quote.sav_case_id);
                              if (c) navigate(`/sav/${quote.sav_case_id}`);
                              else navigate(`/sav/${quote.sav_case_id}`);
                            }}
                          >
                            {quote.sav_case_numero || quote.sav_case_id?.substring(0, 8)}
                          </span>
                        </TableCell>
                        <TableCell>{quote.client_nom || "-"}</TableCell>
                        <TableCell>{new Date(quote.created_at).toLocaleDateString("fr-FR")}</TableCell>
                        <TableCell>
                          <Badge className={config.color}>{config.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {quote.total_ttc?.toFixed(2)}€
                        </TableCell>
                        <TableCell>
                          {quote.facture_numero ? (
                            <Badge className="bg-indigo-100 text-indigo-700">{quote.facture_numero}</Badge>
                          ) : (quote.statut === "accepte" || quote.statut === "envoye") ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setConvertingQuoteId(quote.id); setInvoicePaymentMode("cb"); }}
                              data-testid={`invoice-quote-${quote.id}`}
                              className="gap-1"
                            >
                              <Receipt className="h-3.5 w-3.5" />
                              Facturer
                            </Button>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {quote.statut === "brouillon" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateStatus(quote.id, "envoye")}
                                data-testid={`send-quote-${quote.id}`}
                                title="Envoyer"
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            {quote.statut === "envoye" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateStatus(quote.id, "accepte")}
                                  data-testid={`accept-quote-${quote.id}`}
                                  title="Accepter"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateStatus(quote.id, "refuse")}
                                  data-testid={`refuse-quote-${quote.id}`}
                                  title="Refuser"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <button
                              onClick={() => downloadPdf(`/api/pdf/devis/${quote.id}`, `Devis_${quote.numero}.pdf`)}
                              disabled={downloadingPdf === `/api/pdf/devis/${quote.id}`}
                              className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                              data-testid={`pdf-quote-${quote.id}`}
                              title="Télécharger PDF"
                            >
                              {downloadingPdf === `/api/pdf/devis/${quote.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cards - mobile */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : filteredQuotes.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="font-medium">Aucun devis</p>
              <Button variant="link" onClick={openCreateDialog}>
                Créer votre premier devis
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredQuotes.map((quote) => {
            const config = statusConfig[quote.statut] || statusConfig.brouillon;
            return (
              <Card key={quote.id} data-testid={`quote-card-${quote.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-mono font-semibold">{quote.numero}</p>
                      <p className="text-sm text-muted-foreground">{quote.client_nom}</p>
                      <p className="text-xs text-muted-foreground">
                        {quote.sav_case_numero} - {new Date(quote.created_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                    <Badge className={config.color}>{config.label}</Badge>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-lg font-bold font-mono">{quote.total_ttc?.toFixed(2)}€</span>
                    <div className="flex gap-2">
                      {quote.statut === "brouillon" && (
                        <Button size="sm" onClick={() => updateStatus(quote.id, "envoye")} data-testid={`m-send-${quote.id}`}>
                          <Send className="h-4 w-4 mr-1" /> Envoyer
                        </Button>
                      )}
                      {quote.statut === "envoye" && (
                        <>
                          <Button size="sm" onClick={() => updateStatus(quote.id, "accepte")} data-testid={`m-accept-${quote.id}`}>
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => updateStatus(quote.id, "refuse")} data-testid={`m-refuse-${quote.id}`}>
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {(quote.statut === "accepte" || quote.statut === "envoye") && !quote.facture_numero && (
                        <Button size="sm" variant="outline" onClick={() => { setConvertingQuoteId(quote.id); setInvoicePaymentMode("cb"); }} data-testid={`m-invoice-${quote.id}`}>
                          <Receipt className="h-4 w-4 mr-1" /> Facturer
                        </Button>
                      )}
                      {quote.facture_numero && (
                        <Badge className="bg-indigo-100 text-indigo-700 self-center">{quote.facture_numero}</Badge>
                      )}
                      <button onClick={() => downloadPdf(`/api/pdf/devis/${quote.id}`, `Devis_${quote.numero}.pdf`)} disabled={downloadingPdf === `/api/pdf/devis/${quote.id}`} className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent hover:text-accent-foreground disabled:opacity-50" data-testid={`m-pdf-${quote.id}`}>
                        {downloadingPdf === `/api/pdf/devis/${quote.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Quote Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>

      {/* Convert to Invoice Modal (native) */}
      {convertingQuoteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setConvertingQuoteId(null)}>
          <div className="bg-background rounded-lg shadow-lg p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()} data-testid="invoice-modal">
            <h3 className="text-lg font-semibold mb-1">Convertir en facture</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Devis {quotes.find(q => q.id === convertingQuoteId)?.numero} — {quotes.find(q => q.id === convertingQuoteId)?.total_ttc?.toFixed(2)}€
            </p>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Mode de paiement</label>
                <select
                  value={invoicePaymentMode}
                  onChange={(e) => setInvoicePaymentMode(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="invoice-payment-select"
                >
                  <option value="cb">CB</option>
                  <option value="especes">Espèces</option>
                  <option value="cheque">Chèque</option>
                  <option value="virement">Virement</option>
                  <option value="en_attente">En attente de paiement</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setConvertingQuoteId(null)}>
                  Annuler
                </Button>
                <Button className="flex-1" onClick={() => convertToInvoice(convertingQuoteId)} data-testid="confirm-invoice-btn">
                  <Receipt className="h-4 w-4 mr-2" />
                  Facturer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Quote Dialog - Radix */}        <DialogContent className="max-w-lg sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={submitQuote}>
            <DialogHeader>
              <DialogTitle>Nouveau devis</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* SAV Case Selection */}
              <div className="space-y-2">
                <Label>Dossier SAV *</Label>
                <Select value={selectedCaseId} onValueChange={setSelectedCaseId}>
                  <SelectTrigger data-testid="select-sav-case">
                    <SelectValue placeholder="Sélectionnez un dossier SAV" />
                  </SelectTrigger>
                  <SelectContent>
                    {savCases.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.numero} - {c.client?.prenom} {c.client?.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Quote Lines */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Lignes du devis</Label>
                {quoteLines.map((line, index) => (
                  <div key={index} className="p-3 rounded-lg border bg-secondary/20 space-y-3" data-testid={`quote-line-${index}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">Ligne {index + 1}</span>
                      {quoteLines.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(index)} className="h-8 w-8 p-0 text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Input
                        placeholder="Description"
                        value={line.description}
                        onChange={(e) => updateLine(index, "description", e.target.value)}
                        required
                        data-testid={`line-desc-${index}`}
                      />
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Select value={line.type} onValueChange={(v) => updateLine(index, "type", v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="piece">Pièce</SelectItem>
                            <SelectItem value="main_oeuvre">Main d'œuvre</SelectItem>
                            <SelectItem value="forfait">Forfait</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          min="1"
                          placeholder="Qté"
                          value={line.quantite}
                          onChange={(e) => updateLine(index, "quantite", parseInt(e.target.value) || 1)}
                          data-testid={`line-qty-${index}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Prix unit."
                          value={line.prix_unitaire || ""}
                          onChange={(e) => updateLine(index, "prix_unitaire", parseFloat(e.target.value) || 0)}
                          data-testid={`line-price-${index}`}
                        />
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          placeholder="Remise %"
                          value={line.remise || ""}
                          onChange={(e) => updateLine(index, "remise", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addLine} className="w-full" data-testid="add-quote-line-btn">
                  <Plus className="h-4 w-4 mr-2" /> Ajouter une ligne
                </Button>
              </div>

              <Separator />

              {/* Totals */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label>Remise globale %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={quoteRemise || ""}
                    onChange={(e) => setQuoteRemise(parseFloat(e.target.value) || 0)}
                    className="w-20"
                  />
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Total TTC</p>
                  <p className="text-2xl font-bold" data-testid="quote-total">{calcTotal().toFixed(2)}€</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={creating} data-testid="submit-quote-btn">
                {creating ? "Création..." : "Créer le devis"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotesPage;
